import { useEffect, useRef, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

const faqs = [
  {
    q: 'Ako dlho trvá vytvorenie webu?',
    a: 'Štandardný firemný web dokončíme do 10–12 pracovných dní od schválenia dizajnu. Väčšie projekty individuálne dohodíme — vždy vám povieme realistický termín vopred.',
  },
  {
    q: 'Budem môcť web aktualizovať sám?',
    a: 'Áno. Každý web dodávame s jednoduchým administračným rozhraním, kde môžete meniť texty, fotky a obsah bez akýchkoľvek technických znalostí. Naučíme vás to.',
  },
  {
    q: 'Čo ak sa mi výsledok nebude páčiť?',
    a: 'Dizajn vám ukážeme ešte pred vývojom. K spusteniu pristúpime až po vašom schválení — súčasťou sú iterácie a úpravy. Nikdy nespustíme niečo, s čím nie ste spokojní.',
  },
  {
    q: 'Potrebujem aj hosting a doménu?',
    a: 'Môžeme zabezpečiť všetko — doménu, hosting, SSL certifikát. Ak už niečo máte, radi to využijeme a nastavíme. Hosting vieme zabezpečiť od 9 €/mesiac.',
  },
  {
    q: 'Pomôžete aj s textami na web?',
    a: 'Áno. Pomôžeme vám s textami, ktoré sú jasné, predajné a optimalizované pre Google. Nemusíte byť copywriter — to je naša práca.',
  },
  {
    q: 'Čo je zahrnuté v cene?',
    a: 'Dizajn, vývoj, texty, SEO základy, SSL certifikát a podpora po spustení. Doménu a hosting vieme zabezpečiť za príplatok. Žiadne skryté poplatky.',
  },
  {
    q: 'Robíte aj e-shopy?',
    a: 'Áno, e-shopy sú naša ďalšia špecialita. Ak plánujete predávať online, radi vám poradíme, aká platforma je pre vás najvhodnejšia.',
  },
  {
    q: 'Čo ak potrebujem web rýchlejšie?',
    a: 'Vieme pracovať aj v expresnom režime. Napíšte nám a dohodíme sa — za príplatok vieme skrátiť dobu dodania na 5–7 pracovných dní.',
  },
];

interface TWFaqProps {
  onCtaClick: () => void;
}

export default function TWFaq({ onCtaClick }: TWFaqProps) {
  const { ref, inView } = useInView();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-24 bg-zinc-950">
      <div className="max-w-4xl mx-auto px-6">
        <div ref={ref}>
          <div
            className={`text-center mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-question-answer-line text-zinc-300 text-sm" />
              <span className="text-zinc-300 text-sm font-semibold">Časté otázky</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight">
              Otázky, ktoré dostávame najčastejšie
            </h2>
            <p className="text-zinc-400 text-base">
              Nenašli ste odpoveď?{' '}
              <button onClick={onCtaClick} className="text-orange-400 hover:text-orange-300 font-semibold cursor-pointer underline underline-offset-2">
                Napíšte nám priamo
              </button>
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={faq.q}
                className={`border border-zinc-800 rounded-xl overflow-hidden transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-zinc-900/50 transition-colors duration-200"
                >
                  <span className="text-white font-semibold text-sm sm:text-base leading-snug">{faq.q}</span>
                  <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-zinc-800 border border-zinc-700">
                    <i className={`text-zinc-400 text-sm transition-transform duration-300 ${openIndex === i ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </div>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: openIndex === i ? '300px' : '0px' }}
                >
                  <div className="px-6 pb-5">
                    <p className="text-zinc-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
